import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Column } from "primereact/column";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import FailureApi from "../../services/FailureApi";
import FmeaApi from "../../services/FmeaApi";

// Design System
import DataTable from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import { Tag } from "../../components/Tag/Tag";
import PageHeader from "../../components/PageHeader/PageHeader";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "ListFailures_filters";

interface Failure {
  id: string;
  name: string;
  description: string;
  effect: string;
  operationName: string;
  severity: number;
  occurrence: number;
  detectability: number;
  rpn: number;
  version: number;
  code: string;
  causeIds: string[];
  cause: string;
  causeName?: string;
}

const ListFailures: React.FC = () => {
  const { plantId } = useAuth();
  const [failures, setFailures] = useState<Failure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { t: translateMessages } = useTranslation("messages");
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fmeaSource = searchParams.get("type") || "product";
  const { fmeaId } = useParams<{ fmeaId: string }>();

  const { displayToast } = useToast();
  const [canInheritFamilyFailures, setCanInheritFamilyFailures] = useState<boolean>(false);
  const [causes, setCauses] = useState<{ label: string; value: string; id: string }[]>([]);

  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [failureToDelete, setFailureToDelete] = useState<string | null>(null);

  const [filterCode, setFilterCode] = useState("");
  const [filterOp, setFilterOp] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [filterVersion, setFilterVersion] = useState<number | null>(null);
  const [filterEffect, setFilterEffect] = useState("");
  const [filterRPN, setFilterRPN] = useState<number | null>(null);
  const [filterCauses, setFilterCauses] = useState<string[]>([]);

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    if (!plantId || !fmeaId) return;

    const loadFilters: any = async () => {
      try {
        const respCauses = await FailureApi.GetAllCauses();
        setCauses(respCauses.data.map((u: any) => ({ label: u.name, value: u.id, id: u.id })));
      } catch (err: any) {
        displayToast("error", translateForm("error"), err.message || err, false, 3000);
      }
    };
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId, fmeaId]);

  useEffect(() => {
    if (fmeaSource === "product" && fmeaId) {
      FmeaApi.CheckForFamilyFmea(fmeaId)
        .then((res) => setCanInheritFamilyFailures(res))
        .catch(() => setCanInheritFamilyFailures(false));
    }
  }, [fmeaSource, fmeaId]);

  useEffect(() => {
    if (plantId && fmeaId) {
      if (savedFilters.code) setFilterCode(savedFilters.code.value);
      if (savedFilters.operationName) setFilterOp(savedFilters.operationName.value);
      if (savedFilters.description) setFilterDescription(savedFilters.description.value);
      if (savedFilters.version) setFilterVersion(savedFilters.version.value);
      if (savedFilters.effect) setFilterEffect(savedFilters.effect.value);
      if (savedFilters.rpn) setFilterRPN(savedFilters.rpn.value);
      if (savedFilters.causeIds) setFilterCauses(savedFilters.causeIds.value || []);
      
      fetchFailures(savedFilters, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId, fmeaId]);

  const fetchFailures = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc" | "desc" = sortOrder,
    pageSize: number = rows,
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const description = filters.description?.value ?? undefined;
      const effect = filters.effect?.value ?? undefined;
      const causeIds = filters.causeIds?.value || [];
      const version = filters.version?.value ?? undefined;
      const rpn = filters.rpn?.value ?? undefined;
      const code = filters.code?.value ?? undefined;
      const operationName = filters.operationName?.value ?? undefined;

      const response = await FailureApi.GetByFilterAsync(
        [plantId],
        Math.floor(firstIndex / pageSize) + 1,
        pageSize,
        field,
        direction,
        description,
        effect,
        causeIds,
        undefined, // severity
        undefined, // occurrence
        undefined, // detectability
        operationName,
        version,
        rpn,
        code,
        undefined,
        fmeaId ? [fmeaId] : [],
      );

      const data = Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data?.failures)
          ? response.data.failures
          : [];
          
      setTotalRecords(response.data.total || data.length);
      setFailures(
        data.map((fa: Failure) => ({
          ...fa,
          causeIds: fa.causeIds ? [fa.causeIds] : [],
          causeName: fa.cause ?? "",
        }))
      );
    } catch {
      setFailures([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      code: filterCode ? { value: filterCode } : undefined,
      operationName: filterOp ? { value: filterOp } : undefined,
      description: filterDescription ? { value: filterDescription } : undefined,
      version: filterVersion ? { value: filterVersion } : undefined,
      effect: filterEffect ? { value: filterEffect } : undefined,
      rpn: filterRPN ? { value: filterRPN } : undefined,
      causeIds: filterCauses.length > 0 ? { value: filterCauses } : undefined,
    };

    Object.keys(filters).forEach((key) => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    fetchFailures(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterCode("");
    setFilterOp("");
    setFilterDescription("");
    setFilterVersion(null);
    setFilterEffect("");
    setFilterRPN(null);
    setFilterCauses([]);

    setPage(0);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    fetchFailures({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
    fetchFailures(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  const handleSort = (e: any): void => {
    const dir = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(e.sortField);
    setSortOrder(dir);
    fetchFailures(savedFilters, page, e.sortField, dir);
  };

  const deleteFailure = async (id: string): Promise<void> => {
    try {
      await FailureApi.Delete(id);
      setFailures((prev) => prev.filter((op) => op.id !== id));
      displayToast("success", translateForm("failureSuceffullyDeleted"), "", false, 3000);
    } catch (error: any) {
      const apiErrors = error?.response?.data;
      const messages = Array.isArray(apiErrors)
        ? apiErrors.map((err: any) => `• ${err.data?.data || err.data.data}`).join("\n")
        : error?.data?.data || "Unknown error";
      displayToast("error", translateForm("failureNotDeleted"), messages, false, 5000);
    }
  };

  const confirmDelete = (id: string): void => {
    setFailureToDelete(id);
    setShowConfirmDelete(true);
  };

  const actionBodyTemplate = (rowData: Failure): JSX.Element => (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <IconButton
        icon="edit"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/fmeas/${fmeaId}/failures/${rowData.id}/details`)}
        aria-label={translateGeneral("edit")}
      />
      <IconButton
        icon="delete"
        variant="text"
        severity="secondary"
        onClick={() => confirmDelete(rowData.id)}
        aria-label={translateGeneral("delete")}
      />
      <IconButton
        icon="list_alt"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/fmeas/${fmeaId}/failures/${rowData.id}/action-plans`)}
        aria-label={translateGeneral("manageActionPlan")}
      />
      <IconButton
        icon="attach_file"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/fmeas/${fmeaId}/failures/${rowData.id}/attachments`)}
        aria-label={translateGeneral("manageAttachments")}
      />
      <IconButton
        icon="history"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/fmeas/${fmeaId}/failures/${rowData.id}/versions`)}
        aria-label={translateGeneral("versionHistory")}
      />
    </div>
  );

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("failures")}
        description="Gerencie as falhas associadas a este FMEA"
      >
        <Button
          label={translateForm("back") || "Voltar"}
          icon="arrow_back"
          variant="text"
          severity="secondary"
          onClick={() => navigate(fmeaSource === 'product' ? '/fmeas' : '/family-fmeas')}
        />
        {fmeaSource === "product" && (
          <Button
            label={translateForm("inherit")}
            icon="content_copy"
            severity="secondary"
            disabled={!canInheritFamilyFailures}
            onClick={() => navigate(`/fmeas/${fmeaId}/select-inherited-failures`)}
            title={!canInheritFamilyFailures ? translateMessages("noFamilyFmeaRelation") : undefined}
          />
        )}
        <Button
          label={translateForm("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate(`/fmeas/${fmeaId}/failures/create?type=${fmeaSource}`)}
        />
      </PageHeader>

      <div>
        <ConfirmModal
          visible={showConfirmDelete}
          onHide={() => {
            setShowConfirmDelete(false);
            setFailureToDelete(null);
          }}
          onConfirm={() => failureToDelete && deleteFailure(failureToDelete)}
          title={translateGeneral("confirmation")}
          message={translateForm("failureDeleteMessage")}
          severity="danger"
          confirmLabel={translateForm("Yes")}
          cancelLabel={translateForm("No")}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <FilterPanel
            title="Filtros"
            showFilterIcon
            onApply={handleFilter}
            onClear={handleClearFilters}
          >
            <Input
              type="text"
              label={translateForm("code")}
              value={filterCode}
              onChange={(val: any) => setFilterCode(val)}
            />
            <Input
              type="text"
              label="OP"
              value={filterOp}
              onChange={(val: any) => setFilterOp(val)}
            />
            <Input
              type="text"
              label={translateForm("description")}
              value={filterDescription}
              onChange={(val: any) => setFilterDescription(val)}
            />
            <Input
              type="text"
              label={translateForm("version")}
              value={filterVersion?.toString() || ""}
              onChange={(val: any) => setFilterVersion(val ? Number(val) : null)}
            />
            <Input
              type="text"
              label={translateForm("effect")}
              value={filterEffect}
              onChange={(val: any) => setFilterEffect(val)}
            />
            <Input
              type="text"
              label="RPN"
              value={filterRPN?.toString() || ""}
              onChange={(val: any) => setFilterRPN(val ? Number(val) : null)}
            />
            <Input
              type="multiselect"
              label={translateForm("cause")}
              value={filterCauses}
              options={causes}
              onChange={(val: any) => setFilterCauses(val)}
            />
          </FilterPanel>

          <DataTable
            value={failures}
            paginated
            first={page}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={handlePage}
            rowsPerPageOptions={[10, 20, 50]}
            loading={isLoading}
            emptyMessage={translateForm("noRecordsFound") || "Nenhum registro encontrado"}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            onSort={handleSort}
            resizableColumns
          >
            <Column field="code" header={translateForm("code")} sortable body={(row: Failure) => row.code ?? "N/A"} style={{ minWidth: "8rem" }} />
            <Column field="operationName" header="OP" sortable style={{ minWidth: "12rem" }} />
            <Column field="description" header={translateForm("description")} sortable style={{ minWidth: "16rem" }} />
            <Column field="version" header={translateForm("version")} sortable style={{ minWidth: "6rem" }} />
            <Column field="effect" header={translateForm("effect")} sortable style={{ minWidth: "16rem" }} />
            <Column field="causeIds" header={translateForm("cause")} sortable style={{ minWidth: "12rem" }} body={(row: Failure) => row.causeName?.length ? row.causeName : "N/A"} />
            <Column
              field="rpn"
              header="RPN"
              sortable
              style={{ minWidth: "6rem" }}
              body={(row: Failure) => {
                const value = row.rpn ?? 0;
                let severity: "successInverse" | "warningInverse" | "errorInverse" = "successInverse";
                if (value > 39 && value <= 100) severity = "warningInverse";
                else if (value > 100) severity = "errorInverse";
                
                return (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Tag
                      label={value.toString()}
                      severity={severity}
                    />
                  </div>
                );
              }}
            />
            <Column field="actions" header={translateForm("actions")} body={actionBodyTemplate} style={{ width: "16rem" }} />
          </DataTable>

        </div>
      </div>
    </div>
  );
};

export default ListFailures;
